import { DocsService } from '@affine/core/modules/doc';
import {
  matchTemplates,
  TemplateDocService,
} from '@affine/core/modules/template-doc';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  focusTextModel,
  getInlineEditorByModel,
} from '@blocksuite/affine/rich-text';
import { TextSelection } from '@blocksuite/affine/std';
import {
  type SlashMenuActionContext,
  type SlashMenuActionItem,
  SlashMenuConfigExtension,
  type SlashMenuContext,
} from '@blocksuite/affine/widgets/slash-menu';
import { NotificationProvider } from '@blocksuite/affine-shared/services';
import { TemplateIcon } from '@blocksuite/icons/lit';
import type { ExtensionType } from '@blocksuite/store';
import type { FrameworkProvider } from '@toeverything/infra';

import {
  insertTemplateSnapshot,
  snapshotTemplateContent,
  type TemplateCaret,
} from '../../utils/insert-template';

const ICON_SIZE = { width: '20px', height: '20px' } as const;

/**
 * How long to wait for a template that has not reached this device yet
 * before reporting it as empty.
 */
const TEMPLATE_SYNC_TIMEOUT_MS = 5000;

type TemplateEntry = { id: string; title: string };

export function patchTemplateSlashMenu(
  framework: FrameworkProvider
): ExtensionType {
  const templateDocService = framework.get(TemplateDocService);
  const docsService = framework.get(DocsService);
  const workspaceService = framework.get(WorkspaceService);

  const toast = (ctx: SlashMenuContext, message: string) => {
    ctx.std.getOptional(NotificationProvider)?.toast(message);
  };

  /** Every template except the doc being edited. */
  const listTemplates = (ctx: SlashMenuContext): TemplateEntry[] =>
    templateDocService.list
      .getTemplateDocs()
      .filter(doc => doc.id !== ctx.std.store.id)
      .map(doc => ({
        id: doc.id,
        title: doc.title$.value.trim() || 'Untitled',
      }));

  /**
   * Where `/` was typed. The menu has already deleted `/template …` and left
   * the caret there, but the template loads asynchronously, so capture it
   * before anything else can move it.
   */
  const captureCaret = (ctx: SlashMenuContext): TemplateCaret => {
    const { std, model } = ctx;
    const range = getInlineEditorByModel(std, model)?.getInlineRange();
    if (range) return { blockId: model.id, index: range.index };
    const selection = std.selection.find(TextSelection);
    if (selection?.from.blockId === model.id) {
      return { blockId: model.id, index: selection.from.index };
    }
    return { blockId: model.id, index: model.text?.length ?? 0 };
  };

  /** False once the user has put the caret somewhere else. */
  const isCaretUnmoved = (ctx: SlashMenuContext, caret: TemplateCaret) => {
    const selection = ctx.std.selection.find(TextSelection);
    return (
      !!selection &&
      !selection.to &&
      selection.from.blockId === caret.blockId &&
      selection.from.index === caret.index &&
      selection.from.length === 0
    );
  };

  const loadTemplate = (templateId: string) =>
    docsService.withDoc(templateId, async store => {
      if (store.getBlocksByFlavour('affine:note').length === 0) {
        // withDoc only waits for local storage. A template created on
        // another device may still be on its way from the server; local
        // workspaces resolve this immediately.
        await workspaceService.workspace.engine.doc
          .waitForSynced(
            templateId,
            AbortSignal.timeout(TEMPLATE_SYNC_TIMEOUT_MS)
          )
          .catch(() => {});
      }
      return snapshotTemplateContent(store);
    });

  const insertTemplate = (ctx: SlashMenuContext, template: TemplateEntry) => {
    const { std } = ctx;
    const caret = captureCaret(ctx);

    const run = async () => {
      const snapshot = await loadTemplate(template.id);
      if (!snapshot) {
        toast(ctx, `"${template.title}" has no content to insert`);
        return;
      }
      // The editor went away while the template was loading.
      if (!std.host.isConnected) return;

      const keepCaret = isCaretUnmoved(ctx, caret);
      const result = await insertTemplateSnapshot(std.store, snapshot, caret);
      if (!result) {
        toast(ctx, `Can't insert "${template.title}" here`);
        return;
      }
      if (result.focus && keepCaret) {
        const { blockId, index } = result.focus;
        await std.host.updateComplete;
        focusTextModel(std, blockId, index);
      }
    };

    run().catch(error => {
      console.error(error);
      toast(ctx, `Failed to insert "${template.title}"`);
    });
  };

  const getTemplateItems = (
    ctx: SlashMenuContext,
    actionContext: SlashMenuActionContext
  ): SlashMenuActionItem[] =>
    matchTemplates(listTemplates(ctx), actionContext.argument).map(
      template => ({
        name: template.title,
        description: 'Insert template',
        icon: TemplateIcon(ICON_SIZE),
        action: (actionCtx: SlashMenuContext) =>
          insertTemplate(actionCtx, template),
      })
    );

  return SlashMenuConfigExtension('affine-template', {
    items: [
      {
        name: 'Template',
        icon: TemplateIcon(ICON_SIZE),
        description: 'Insert a template at the cursor',
        group: '3_Page@3',
        searchAlias: ['template', 'templates'],
        acceptsArgument: true,
        // Rows replace this item whenever a template matches, so this only
        // runs when none does.
        action: (ctx, actionContext) => {
          const argument = actionContext?.argument.trim() ?? '';
          if (listTemplates(ctx).length === 0) {
            toast(
              ctx,
              'No templates yet. Turn on "Template" in a doc\'s Info panel to use it here'
            );
            return;
          }
          toast(
            ctx,
            argument
              ? `No template matches "${argument}"`
              : 'Pick a template from the list'
          );
        },
        getArgumentItems: getTemplateItems,
      },
    ],
  });
}
